// The point an object turns about (owner, 2026-10-06). Not set = the centre of its box.
// It is stored on the object (meta.pivot, drawing millimetres) and travels with it: moving,
// turning, scaling, copying and pasting all carry the point along (entityOps / editOps).
import type { Vec } from "../core/geometry";
import type { Doc, Entity } from "../doc/entities";
import { objectBox } from "./align";

type Patch = { id: string; before: Record<string, unknown>; after: Record<string, unknown> };

/** The set turning point of an object, or null when it turns about its box centre. */
export function pivotOf(entity: Entity | undefined): Vec | null {
  const p = entity?.meta?.pivot as { x?: unknown; y?: unknown } | undefined;
  if (!p || typeof p.x !== "number" || typeof p.y !== "number") return null;
  return Number.isFinite(p.x) && Number.isFinite(p.y) ? { x: p.x, y: p.y } : null;
}

/** Centre of the box round `ids` (groups included), or null when nothing has a size. */
export function boxCentre(doc: Doc, ids: Iterable<string>): Vec | null {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const id of ids) {
    const b = objectBox(doc, id);
    if (!b) continue;
    minX = Math.min(minX, b.minX);
    minY = Math.min(minY, b.minY);
    maxX = Math.max(maxX, b.maxX);
    maxY = Math.max(maxY, b.maxY);
  }
  return minX <= maxX ? { x: (minX + maxX) / 2, y: (minY + maxY) / 2 } : null;
}

/**
 * Where a selection turns: one object with a set point turns about that point. Anything
 * else (no point set, or several objects) turns about the centre of the selection box.
 */
export function turnPoint(doc: Doc, ids: Iterable<string>): Vec | null {
  const list = [...ids];
  if (list.length === 1) {
    const own = pivotOf(doc.entities[list[0]!]);
    if (own) return own;
  }
  return boxCentre(doc, list);
}

/** Patch that sets (or, with null, clears) the turning point of one object. */
export function setPivotPatch(doc: Doc, id: string, p: Vec | null): Patch | null {
  const e = doc.entities[id];
  if (!e) return null;
  const meta = { ...(e.meta ?? {}) };
  if (p) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
    meta.pivot = { x: p.x, y: p.y };
  } else {
    if (!("pivot" in meta)) return null;
    delete meta.pivot;
  }
  return { id, before: { meta: e.meta }, after: { meta } };
}

/** Nine points of an object's box: the corners, the middles of the sides and the centre. */
export type PivotSpot = "tl" | "t" | "tr" | "l" | "c" | "r" | "bl" | "b" | "br";
export const PIVOT_SPOTS: PivotSpot[] = ["tl", "t", "tr", "l", "c", "r", "bl", "b", "br"];
export const PIVOT_SPOT_NAMES: Record<PivotSpot, string> = {
  tl: "top-left corner",
  t: "middle of the top",
  tr: "top-right corner",
  l: "middle of the left side",
  c: "centre",
  r: "middle of the right side",
  bl: "bottom-left corner",
  b: "middle of the bottom",
  br: "bottom-right corner"
};

export function spotPoint(box: { minX: number; minY: number; maxX: number; maxY: number }, spot: PivotSpot): Vec {
  const x = spot.endsWith("l") ? box.minX : spot.endsWith("r") ? box.maxX : (box.minX + box.maxX) / 2;
  const y = spot.startsWith("t") ? box.minY : spot.startsWith("b") ? box.maxY : (box.minY + box.maxY) / 2;
  return { x, y };
}

/**
 * Add the moved turning point to an entity's patch (creates one when the entity itself has
 * nothing to patch, e.g. a group). `mp` is the same point map the geometry used.
 */
export function carryPivot(entity: Entity, patch: Patch | null, mp: (p: Vec) => Vec): Patch | null {
  const pv = pivotOf(entity);
  if (!pv) return patch;
  const out = patch ?? { id: entity.id, before: {}, after: {} };
  const metaAfter = (out.after.meta as Record<string, unknown> | undefined) ?? entity.meta ?? {};
  out.before.meta = entity.meta;
  out.after.meta = { ...metaAfter, pivot: mp(pv) };
  return out;
}

/** In-place version for clones (paste, library placement). */
export function shiftPivotInPlace(entity: Entity, d: Vec): void {
  const pv = pivotOf(entity);
  if (pv) entity.meta = { ...(entity.meta ?? {}), pivot: { x: pv.x + d.x, y: pv.y + d.y } };
}
